import { isTerminalOperationState } from "../api/schemas";
import { safeOperationSnapshot } from "../store/protocolOperations";

export const RECONCILE_ONLY_OPERATION_STATES = Object.freeze([
  "PREPARING",
  "SUBMITTED",
  "MINED",
  "VERIFYING",
]);

export const OPERATION_VERIFICATION_PENDING =
  "OPERATION_VERIFICATION_PENDING";

export class OperationVerificationPendingError extends Error {
  constructor(operation, label = "operation") {
    const state = String(operation?.state ?? "UNKNOWN").toUpperCase();
    super(`${label} verification is still pending: ${state}`);
    this.name = "OperationVerificationPendingError";
    this.code = OPERATION_VERIFICATION_PENDING;
    this.operation = operation;
    this.operationState = state;
    this.retryable = true;
  }
}

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const normalizedPollCount = (poll, maxPolls) => {
  if (!poll) return 1;
  const parsed = Number(maxPolls);
  if (!Number.isSafeInteger(parsed) || parsed < 1) {
    throw new Error("maxPolls must be a positive safe integer");
  }
  return parsed;
};

const normalizedPollInterval = (pollIntervalMs) => {
  const parsed = Number(pollIntervalMs);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    throw new Error("pollIntervalMs must be a non-negative safe integer");
  }
  return parsed;
};

export const isReconcileOnlyOperationState = (state) =>
  RECONCILE_ONLY_OPERATION_STATES.includes(String(state ?? "").toUpperCase());

export const isOperationVerificationPendingError = (error) =>
  error?.code === OPERATION_VERIFICATION_PENDING;

export const reconcileLatestOperation = async (
  operationClient,
  operationId
) => {
  let latest;
  if (operationClient?.reconcileAndFetchOperation) {
    latest = await operationClient.reconcileAndFetchOperation(operationId);
  } else {
    const acknowledgement = await operationClient.reconcileOperation(operationId);
    if (acknowledgement?.operationId !== operationId) {
      throw new Error("reconciliation operation id mismatch");
    }
    latest = operationClient.fetchOperation
      ? await operationClient.fetchOperation(operationId)
      : acknowledgement;
  }
  if (latest?.operationId !== operationId) {
    throw new Error("reconciliation operation id mismatch");
  }
  return latest;
};

export const reconcileOperationUntilTerminal = async ({
  operationClient,
  operationId,
  store = null,
  poll = false,
  maxPolls = 3,
  pollIntervalMs = 1000,
} = {}) => {
  if (
    !operationId ||
    (!operationClient?.reconcileAndFetchOperation &&
      !operationClient?.reconcileOperation)
  ) {
    throw new Error("operation client is incomplete");
  }

  const attempts = normalizedPollCount(poll, maxPolls);
  const interval = normalizedPollInterval(pollIntervalMs);
  let latest = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt > 0 && interval > 0) {
      await wait(interval);
    }
    latest = await reconcileLatestOperation(operationClient, operationId);
    store?.update?.(safeOperationSnapshot(latest));
    if (isTerminalOperationState(latest.state)) {
      break;
    }
  }
  if (poll && isReconcileOnlyOperationState(latest?.state)) {
    throw new OperationVerificationPendingError(latest);
  }
  return latest;
};
